import { Fragment } from "react";
import { Col } from "reactstrap";

import AppTablePagination from "../AppTable/AppTablePagination";
import useRecordMetadata from "../../hooks/useRecordMetadata";
import { ReusableViewIDs } from "../../types/PageInterfaces";
import SkeletonLoader from "../../elements/SkeletonLoader";
import AppEmptyState from "../../elements/AppEmptyState";
import useRecordImage from "../../hooks/useRecordImage";
import AppTableListHeader from "./AppTableListHeader";
import AppTableListItem from "./AppTableListItem";
import useModule from "../../hooks/useModule";

interface Props {
  moduleAPIName: string;
  query: any;
  selectableRows: boolean;
  selectedRecords?: Array<any>;
  data: any[];
  pagination: any;
  loading: boolean;
  error: any;
  errorMsg: string;
  isSelection: any;
  loadTableInformation: any;
  toggleBulkApplyTags: any;
  toggleTemplateCSVModal: any;
  toggleMergeDrawer: any;
  selectedRecordsLength: any;
  separateName?: boolean;
  multiSubitemFieldAPIName?: string;
  parentRecord?: any;
  columnAdd?: any;
  columnAddMessage: string | null;
  view?: any;
  containerClasses?: string;
  nameField?: string;
  includeHeader?: boolean;
  onColumnAdd: (data: any) => void;
  onColumnRemove: (data: any) => void;
  onSort: (field: string, order: string) => void;
  onUpdateQuery: (query?: any) => void;
  onOpenRecordName: (data?: any) => void;
  toggleUpdateRecordsModal: () => void;
  toggleCreateRecordsDrawer: () => void;
  deleteSelectedRecords: () => void;
  handleDuplicateRecord: (data: any) => void;
  setExportDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  setQuery: React.Dispatch<React.SetStateAction<any>>;
  setSelectedRecords: React.Dispatch<React.SetStateAction<any>>;
  reusableViews?: ReusableViewIDs;
}

const AppTableList = ({
  moduleAPIName,
  query,
  setQuery,
  selectableRows,
  selectedRecords,
  data,
  pagination,
  loading,
  error,
  containerClasses,
  toggleCreateRecordsDrawer,
  onOpenRecordName,
  isSelection,
  setExportDrawer,
  loadTableInformation,
  toggleUpdateRecordsModal,
  toggleBulkApplyTags,
  toggleTemplateCSVModal,
  deleteSelectedRecords,
  toggleMergeDrawer,
  handleDuplicateRecord,
  selectedRecordsLength,
  separateName = false,
  multiSubitemFieldAPIName = "",
  parentRecord,
  columnAdd,
  columnAddMessage,
  view,
  nameField,
  setSelectedRecords,
  onColumnAdd,
  onColumnRemove,
  includeHeader = true,
  reusableViews,
}: Props) => {
  const { module } = useModule(moduleAPIName);
  const { imageField, imageFieldPresentation } = useRecordImage({ module });
  const { metadataField, metadataFieldType } = useRecordMetadata({ module });

  return (
    <div className="d-flex w-100 justify-content-center align-items-center">
      {loading ? (
        <Col className="p-0 m-0 ml--3">
          {Array(8)
            .fill(0)
            .map((_, i) => (
              <SkeletonLoader key={`loader-table-list-${i}`} height={40} />
            ))}
        </Col>
      ) : (
        <>
          {data.length ? (
            <Col className="d-flex flex-column justify-content-between p-0 my-0 ml--3">
              <div>
                {includeHeader && (
                  <AppTableListHeader
                    view={view}
                    isSelection={isSelection}
                    setExportDrawer={setExportDrawer}
                    loadTableInformation={loadTableInformation}
                    toggleUpdateRecordsModal={toggleUpdateRecordsModal}
                    toggleBulkApplyTags={toggleBulkApplyTags}
                    toggleTemplateCSVModal={toggleTemplateCSVModal}
                    deleteSelectedRecords={deleteSelectedRecords}
                    toggleMergeDrawer={toggleMergeDrawer}
                    handleDuplicateRecord={handleDuplicateRecord}
                    selectedRecordsLength={selectedRecordsLength}
                    selectedRecords={selectedRecords}
                    onSelectAll={() => {
                      for (let i = 0; i < data.length; i++) {
                        setSelectedRecords((prev: any) => ({
                          ...prev,
                          [i]: true,
                        }));
                      }
                    }}
                    onDeselectAll={() => setSelectedRecords({})}
                    selectable={selectableRows}
                  />
                )}
              </div>
              <div>
                {data?.map((record, i) => (
                  <Fragment key={record.id}>
                    <AppTableListItem
                      index={i}
                      item={record}
                      onOpenRecordName={onOpenRecordName}
                      module={module}
                      selectedRecords={selectedRecords || []}
                      onSelect={() =>
                        setSelectedRecords((prev: any) => ({
                          ...prev,
                          [i]: true,
                        }))
                      }
                      onDeselect={() =>
                        setSelectedRecords((prev: any) => {
                          const newRecords = { ...prev };
                          delete newRecords[i];
                          return newRecords;
                        })
                      }
                      selectableRows={selectableRows}
                      imageField={imageField}
                      imageFieldPresentation={imageFieldPresentation}
                      metadataField={metadataField}
                      metadataFieldType={metadataFieldType}
                      separateName={separateName}
                      multiSubitemFieldAPIName={multiSubitemFieldAPIName}
                      subitemParentID={parentRecord?.id}
                      fields={view?.Query?.Fields || query?.Fields}
                      columnAdd={columnAdd}
                      columnAddMessage={columnAddMessage}
                      onColumnAdd={onColumnAdd}
                      onColumnRemove={onColumnRemove}
                      nameField={nameField}
                      reusableViews={reusableViews}
                    />
                  </Fragment>
                ))}
              </div>
              <div
                className={`${
                  containerClasses === "pr-4"
                    ? "ml--3 ml-sm--4 pl-sm-2 text-center text-sm-left pr-0 mr-sm--2"
                    : containerClasses
                }`}
              >
                {pagination && (
                  <AppTablePagination
                    records={data}
                    pagination={pagination}
                    query={query}
                    setQuery={setQuery}
                    error={error}
                    setSelectedRecords={setSelectedRecords}
                    loading={loading}
                    module={module}
                  />
                )}
              </div>
            </Col>
          ) : (
            <>
              {query?.Filters?.length ? (
                <div style={{ color: "unset", fontSize: 12 }}>
                  {loading ? (
                    <SkeletonLoader />
                  ) : (
                    "No records available based on your search."
                  )}
                </div>
              ) : (
                <Col className="p-0 m-0">
                  <AppEmptyState
                    title="No Records"
                    description="Get started by creating your first record"
                    buttonTitle="Create"
                    onClick={toggleCreateRecordsDrawer}
                    icon="mdi-sitemap"
                  />
                </Col>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AppTableList;
